import type { Plan } from '@common/api/billingService';
import type { Translate } from '@client-app/i18n';
import chatGptLogoSrc from '../../../public/brand/ai-platforms/chatgpt.svg';
import claudeLogoSrc from '../../../public/brand/ai-platforms/claude.svg';
import workBuddyLogoSrc from '../../../public/brand/ai-platforms/workbuddy.svg';
import type { PlanData } from './PricingCard';

export const PLUGIN_MONTHLY_PRICE_USD = 19;
export const PLUGIN_BILLING_CYCLE = 'monthly' as const;
export const PLUGIN_EXCLUDED_PRICING_INTEGRATIONS = ['Slack', 'Feishu', 'DingTalk'] as const;
export const PLUGIN_PLAN_FALLBACK_NAME = 'Solo';
export const PLUGIN_PUBLIC_PLAN_CODES = ['PLUGIN_SOLO_19', 'PLUGIN_TEAM_99'] as const;
const LEGACY_PLUGIN_SOLO_PLAN_CODES = new Set(['PLUGIN_PLAN']);

type PluginBillingCycle = 'monthly' | 'yearly';

export const PLUGIN_AI_PLATFORMS = [
    { name: 'ChatGPT', iconSrc: chatGptLogoSrc },
    { name: 'Claude', iconSrc: claudeLogoSrc },
    { name: 'WorkBuddy', iconSrc: workBuddyLogoSrc },
] as const;

export const PLUGIN_FALLBACK_PLANS: Plan[] = [
    {
        id: '__plugin_fallback_solo_19__',
        code: 'PLUGIN_SOLO_19',
        productCode: 'PLUGIN',
        name: 'Solo',
        tier: 'PLUGIN_SOLO_19',
        pointsQuota: 0,
        priceCents: 1900,
        displayMonthlyAmountCents: 1900,
        priceUsd: 19,
        chargeAmountUsd: 19,
        displayMonthlyAmountUsd: 19,
        currency: 'USD',
        billingCycle: PLUGIN_BILLING_CYCLE,
        isActive: true,
        isDefault: true,
        createdAt: '',
        rules: { planFamily: 'solo', memberLimit: 1 },
    },
    {
        id: '__plugin_fallback_team_99__',
        code: 'PLUGIN_TEAM_99',
        productCode: 'PLUGIN',
        name: 'Team',
        tier: 'PLUGIN_TEAM_99',
        pointsQuota: 0,
        priceCents: 9900,
        displayMonthlyAmountCents: 9900,
        priceUsd: 99,
        chargeAmountUsd: 99,
        displayMonthlyAmountUsd: 99,
        currency: 'USD',
        billingCycle: PLUGIN_BILLING_CYCLE,
        isActive: true,
        isDefault: false,
        createdAt: '',
        rules: { planFamily: 'team', memberLimit: 10 },
    },
];

interface BuildPluginPricingPlanDataInput {
    t: Translate;
    buttonText: string;
    planName?: string | null;
    price?: number;
    bubbleText?: string | null;
    planFamily?: 'solo' | 'team';
    memberLimit?: number | null;
}

export const buildPluginPricingPlanData = ({
    t,
    buttonText,
    planName,
    price = PLUGIN_MONTHLY_PRICE_USD,
    bubbleText = null,
    planFamily = 'solo',
    memberLimit = 1,
}: BuildPluginPricingPlanDataInput): PlanData => ({
    name: planName?.trim() || (planFamily === 'team' ? 'Team' : PLUGIN_PLAN_FALLBACK_NAME),
    price,
    description: t(
        planFamily === 'team'
            ? 'website.pricing.pluginTeamPlanDescription'
            : 'website.pricing.pluginSoloPlanDescription',
    ),
    buttonText,
    bubbleText,
    featureMode: 'grouped',
    featureEmphasis: 'numbers-only',
    // Solo and Team have a different number of workspace benefits. Keep the
    // shared AI-app and integration sections aligned when the cards sit side by side.
    alignFeatureSections: true,
    workspaceGroupLabel: t('website.pricing.pluginWorkspaceConnectionsTitle'),
    featureGroups: {
        workspace: [
            {
                id: 'workspace-user',
                label: planFamily === 'team' && memberLimit
                    ? t('website.pricing.pluginFeatureTeamUsers', { count: memberLimit })
                    : t('website.pricing.pluginFeatureUser'),
            },
            { id: 'connections', label: t('website.pricing.pluginFeatureConnections') },
            { id: 'manage-connections', label: t('website.pricing.pluginFeatureManageConnections') },
            { id: 'agent-permissions', label: t('website.pricing.pluginFeatureAgent') },
            { id: 'admin-review', label: t('website.pricing.pluginFeatureReview') },
            ...(planFamily === 'team'
                ? [{ id: 'team-collaboration', label: t('website.pricing.pluginFeatureTeamCollaboration') }]
                : []),
        ],
        workUnits: [],
        trainingAndIntegrations: [],
    },
    builtInCapabilitiesLabel: t('website.pricing.pluginAiPlatformsTitle'),
    builtInModelsLayout: 'horizontal',
    builtInModels: [
        ...PLUGIN_AI_PLATFORMS.map(platform => ({
            name: platform.name,
            iconSrc: platform.iconSrc,
            capabilityLabel: platform.name,
        })),
        {
            name: 'and-more',
            capabilityLabel: t('website.pricing.andMore'),
        },
    ],
    showIntegrations: true,
    hideTrainingAndIntegrationsHeading: true,
    integrationsPresentation: 'section',
    excludedIntegrations: planFamily === 'team' ? [] : PLUGIN_EXCLUDED_PRICING_INTEGRATIONS,
});

const normalizeBillingCycle = (value?: string | null): PluginBillingCycle => (
    String(value || '').toLowerCase() === 'yearly' ? 'yearly' : 'monthly'
);

const normalizePlanCode = (value?: string | null): string => (
    String(value || '').trim().toUpperCase()
);

const normalizePluginCurrentPlanCode = (value?: string | null): string => {
    const normalized = normalizePlanCode(value);
    return LEGACY_PLUGIN_SOLO_PLAN_CODES.has(normalized)
        ? 'PLUGIN_SOLO_19'
        : normalized;
};

export const resolvePluginMonthlyPlan = (
    plans: Plan[],
    planCode?: string | null,
): Plan | null => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    if (!normalizedPlanCode) {
        return null;
    }

    return plans.find(plan => (
        plan.isActive
        && normalizePlanCode(plan.code) === normalizedPlanCode
        && normalizeBillingCycle(plan.billingCycle) === PLUGIN_BILLING_CYCLE
    )) ?? null;
};

export const resolvePluginCurrentPlanCode = (
    ...planCodes: Array<string | null | undefined>
): string | null => {
    const currentPlanCode = planCodes
        .map(normalizePluginCurrentPlanCode)
        .find(Boolean);
    return currentPlanCode || null;
};

export const isPluginCurrentPlan = (
    plan: Plan,
    currentPlanCode?: string | null,
): boolean => normalizePlanCode(plan.code) === normalizePluginCurrentPlanCode(currentPlanCode);

// Plugin plan API values expose explicit USD display fields. Checkout continues
// to use the plan ID/Stripe price ID rather than any display amount.
const getDisplayMonthlyPriceUsd = (plan: Plan): number => {
    if (Number.isFinite(Number(plan.displayMonthlyAmountUsd))) {
        return Number(plan.displayMonthlyAmountUsd);
    }

    if (normalizeBillingCycle(plan.billingCycle) !== 'monthly') {
        return 0;
    }
    return Number(plan.chargeAmountUsd ?? plan.priceUsd ?? 0);
};

export const resolvePluginBillingPlan = (
    plans: Plan[],
): Plan | null => {
    const expectedMonthlyPriceUsd = PLUGIN_MONTHLY_PRICE_USD;
    return resolvePluginBillingPlans(plans).find(plan => (
        getDisplayMonthlyPriceUsd(plan) === expectedMonthlyPriceUsd
    )) ?? null;
};

const resolvePlanFamily = (plan: Plan): 'solo' | 'team' => {
    const family = String(plan.rules?.planFamily || '').toLowerCase();
    if (family === 'team' || family === 'solo') {
        return family;
    }
    return plan.code.toUpperCase().includes('TEAM') ? 'team' : 'solo';
};

export const resolvePluginBillingPlans = (plans: Plan[]): Plan[] => plans
    .filter(plan => (
        plan.isActive
        && normalizeBillingCycle(plan.billingCycle) === PLUGIN_BILLING_CYCLE
        && (plan.productCode ? plan.productCode.toUpperCase() === 'PLUGIN' : plan.code.toUpperCase().startsWith('PLUGIN_'))
        && PLUGIN_PUBLIC_PLAN_CODES.some(code => code === normalizePlanCode(plan.code))
    ))
    .sort((left, right) => getDisplayMonthlyPriceUsd(left) - getDisplayMonthlyPriceUsd(right))
    .slice(0, PLUGIN_PUBLIC_PLAN_CODES.length);

export const getPluginPlanFamily = (plan: Plan): 'solo' | 'team' => resolvePlanFamily(plan);

export const resolvePluginPlanForFamily = (
    plans: Plan[],
    family: 'solo' | 'team',
    selectedPlanId?: string | null,
): Plan | null => {
    const familyPlans = resolvePluginBillingPlans(plans).filter(plan => (
        getPluginPlanFamily(plan) === family
    ));
    return familyPlans.find(plan => plan.id === selectedPlanId)
        ?? familyPlans[familyPlans.length - 1]
        ?? null;
};

export const getPluginPlanMemberLimit = (plan: Plan): number | null => {
    const value = Number(plan.rules?.memberLimit);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
};

export const getPluginPlanPrice = (plan: Plan): number => (
    getDisplayMonthlyPriceUsd(plan)
);
